import { useAuth, useClerk, useOrganizationList, useUser } from "@clerk/react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { logEvent } from "@/lib/eventLogger";
import { cn } from "@/utils/cn";
import { Icon } from "@/components/Icon/Icon";
import type { IconName } from "@/utils/types/ui";

// Closed enum for the `item` property on `user_settings_menu_item_clicked`.
// Mirrors the menu structure in the popover below — keep in sync if you
// add/rename/remove a row.
export type UserSettingsMenuItem =
  | "switch_profile_personal"
  | "switch_profile_org"
  | "manage_ingredient_library"
  | "manage_chromatography"
  | "preferences"
  | "see_pricing_plan"
  | "help"
  | "log_out";

type SettingsModalProps = {
  onClose: () => void;
};

export function SettingsModal({ onClose }: SettingsModalProps) {
  const { user, isLoaded: userLoaded } = useUser();
  const { signOut } = useClerk();
  const { orgId } = useAuth();
  const { isLoaded: listLoaded, setActive, userMemberships } = useOrganizationList({
    userMemberships: { infinite: true },
  });
  const navigate = useNavigate();

  // Fire once when the popover mounts (i.e., user opened the menu).
  useEffect(() => {
    logEvent("user_settings_opened");
  }, []);

  const logMenuClick = (item: UserSettingsMenuItem) => {
    logEvent("user_settings_menu_item_clicked", { item });
  };

  if (!userLoaded || !listLoaded || !user) return null;

  const email = user.primaryEmailAddress?.emailAddress ?? "";

  const handleSwitchPersonal = async () => {
    if (!setActive) return;
    logMenuClick("switch_profile_personal");
    await setActive({ organization: null });
    onClose();
  };

  const handleSwitchOrg = async (organizationId: string) => {
    if (!setActive) return;
    logMenuClick("switch_profile_org");
    await setActive({ organization: organizationId });
    onClose();
  };

  const handleLogout = async () => {
    logMenuClick("log_out");
    onClose();
    await signOut();
    navigate("/sign-in", { replace: true });
  };

  return (
    <div className="settings-popover-scrim" onClick={onClose}>
      <div
        className="settings-popover slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — current account email */}
        <div className="settings-popover-header">
          <span className="email">{email}</span>
          <span className="badge">Account</span>
        </div>

        {/* Group 1 — Profiles. Active profile is rendered first
            (mirrors the design); Personal sits below as a switch target.  */}
        <div style={{ padding: 6 }}>
          {userMemberships.data?.map((mem) => (
            <ProfileRow
              key={mem.id}
              org={mem.organization.name}
              sub={`Team · ${mem.role.replace(/^org:/, "")}`}
              initials={mem.organization.name.slice(0, 2).toUpperCase()}
              active={orgId === mem.organization.id}
              onClick={() => handleSwitchOrg(mem.organization.id)}
            />
          ))}
          <ProfileRow
            org="Personal"
            sub="Free plan"
            initials="P"
            avatarColor="var(--ink-3)"
            avatarFg="#fff"
            active={!orgId}
            onClick={handleSwitchPersonal}
          />
        </div>

        <div className="settings-popover-divider" />

        {/* Group 2 — Workspace */}
        <div style={{ padding: "0 6px" }}>
          <MenuItem
            icon="box"
            label="Manage Substance Library"
            onClick={() => {
              logMenuClick("manage_ingredient_library");
              onClose();
              navigate("/ingredients");
            }}
          />
          <MenuItem
            icon="atom"
            label="Manage Chromatography"
            onClick={() => {
              logMenuClick("manage_chromatography");
              onClose();
              navigate("/instruments");
            }}
          />
          <MenuItem
            icon="settings"
            label="Preferences"
            shortcut="⌘ ,"
            onClick={() => logMenuClick("preferences")}
          />
        </div>

        <div className="settings-popover-divider" />

        {/* Group 3 — Account / help */}
        <div style={{ padding: "0 6px" }}>
          <MenuItem
            icon="zap"
            label="See pricing plan"
            trailing={<Icon name="external" size={11} />}
            onClick={() => logMenuClick("see_pricing_plan")}
          />
          <MenuItem
            icon="info"
            label="Help"
            trailing={<Icon name="chevron-r" size={12} />}
            onClick={() => logMenuClick("help")}
          />
        </div>

        <div className="settings-popover-divider" />

        {/* Group 4 — Destructive */}
        <div style={{ padding: "0 6px 6px" }}>
          <MenuItem icon="arrow-r" label="Log out" danger onClick={handleLogout} />
        </div>
      </div>
    </div>
  );
}

type ProfileRowProps = {
  org: string;
  sub: string;
  initials: string;
  active?: boolean;
  avatarColor?: string;
  avatarFg?: string;
  onClick?: () => void;
};

function ProfileRow({
  org,
  sub,
  initials,
  active,
  avatarColor,
  avatarFg,
  onClick,
}: ProfileRowProps) {
  return (
    <button
      type="button"
      className={cn("settings-row", active && "active")}
      onClick={onClick}
    >
      <div
        className="row-avatar"
        style={
          avatarColor
            ? { background: avatarColor, color: avatarFg || "#fff" }
            : undefined
        }
      >
        {initials}
      </div>
      <div className="row-text">
        <span className="row-org">{org}</span>
        <span className="row-sub">{sub}</span>
      </div>
      {active && (
        <span className="row-check">
          <Icon name="check" size={14} />
        </span>
      )}
    </button>
  );
}

type MenuItemProps = {
  icon: IconName;
  label: string;
  shortcut?: string;
  trailing?: React.ReactNode;
  onClick?: () => void;
  danger?: boolean;
};

function MenuItem({
  icon,
  label,
  shortcut,
  trailing,
  onClick,
  danger,
}: MenuItemProps) {
  return (
    <button
      type="button"
      className={cn("settings-menu-item", danger && "danger")}
      onClick={onClick}
    >
      <span className="item-icon">
        <Icon name={icon} size={14} />
      </span>
      <span className="item-label">{label}</span>
      {shortcut && <span className="item-shortcut">{shortcut}</span>}
      {trailing && <span className="item-trailing">{trailing}</span>}
    </button>
  );
}
